// #564 Einsatz-Tracking: „Im Eigentümerportal zeigen“ am Ticket (neue Tickets an, ältere aus – Entscheidung
// 25.10.2026; rechtlich sensible nie) und Kostenfreigabe (T4): geschätzte Kosten über der Wertgrenze des
// berechtigten Eigentümers warten auf seine Freigabe im Portal. Es wird nichts verschickt.
import { useEffect, useState } from 'react';
import { ArrowCounterClockwise, Buildings, CheckCircle, XCircle } from '@phosphor-icons/react';
import { WidgetHuelle } from '../../../../components/ui/WidgetHuelle';
import { IosSchalter } from '../../../../components/ui/IosSchalter';
import { supabase } from '../../../../lib/supabase/client';
import { leseBetrag } from '../../../../lib/portal/einsaetze';

interface Stand {
  fuer_eigentuemer_sichtbar: boolean; ist_rechtlich_sensibel: boolean | null;
  geschaetzte_kosten: number | null; kosten_entscheidung: 'freigegeben' | 'abgelehnt' | null;
  kosten_entschieden_am: string | null; kosten_begruendung: string | null;
}
const SPALTEN = 'fuer_eigentuemer_sichtbar, ist_rechtlich_sensibel, geschaetzte_kosten, kosten_entscheidung, kosten_entschieden_am, kosten_begruendung';
const EURO = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const DATUM = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function WidgetEigentuemerPortal({ ticketId, userId, onMeldung }: { ticketId: string; userId?: string; onMeldung?: (text: string, fehler?: boolean) => void }) {
  const [stand, setStand] = useState<Stand | null>(null);
  const [betrag, setBetrag] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = async () => {
    const { data } = await supabase.from('tickets').select(SPALTEN).eq('id', ticketId).maybeSingle();
    if (!data) return;
    setStand(data as Stand);
    setBetrag(data.geschaetzte_kosten != null ? Number(data.geschaetzte_kosten).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
  };
  useEffect(() => { void laden(); }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stand) return null;
  const sensibel = !!stand.ist_rechtlich_sensibel;

  const speichere = async (felder: Partial<Stand>, erfolg: string, verlauf?: string): Promise<boolean> => {
    setSpeichert(true); setFehler(null);
    const { data, error } = await supabase.from('tickets').update(felder).eq('id', ticketId).select(SPALTEN);
    setSpeichert(false);
    const problem = error?.message ?? (!data?.length ? 'Keine Berechtigung, dieses Ticket zu ändern.' : null);
    if (problem) { setFehler(problem); onMeldung?.(problem, true); return false; }
    setStand(data![0] as Stand);
    if (verlauf) await supabase.from('ticket_historie').insert({ ticket_id: ticketId, benutzer_id: userId ?? null, aktion: 'kosten_entscheidung', neuer_wert: verlauf });
    onMeldung?.(erfolg);
    return true;
  };

  const betragSpeichern = async () => {
    const n = leseBetrag(betrag);
    if (n === undefined) { setFehler('Bitte einen gültigen Betrag eingeben, z. B. 1.250,00'); return; }
    if (n === (stand.geschaetzte_kosten != null ? Number(stand.geschaetzte_kosten) : null)) return;
    await speichere({ geschaetzte_kosten: n }, n === null ? 'Geschätzte Kosten entfernt' : `Geschätzte Kosten: ${EURO(n)}`);
  };

  const zuruecksetzen = () => speichere(
    { kosten_entscheidung: null, kosten_entschieden_am: null, kosten_begruendung: null },
    'Entscheidung zurückgesetzt', 'Kostenentscheidung des Eigentümers zurückgesetzt');

  return (
    <WidgetHuelle titel="Eigentümerportal" icon={Buildings} style={{ flexShrink: 0 }}>
      <div className="flex items-center" style={{ gap: 10 }}>
        <span className="flex-1" style={{ fontSize: 13.5, fontWeight: 700 }}>Im Eigentümerportal zeigen</span>
        <IosSchalter an={stand.fuer_eigentuemer_sichtbar && !sensibel} label="Im Eigentümerportal zeigen" gesperrt={speichert || sensibel}
          onChange={(an) => void speichere({ fuer_eigentuemer_sichtbar: an }, an ? 'Im Eigentümerportal sichtbar' : 'Im Eigentümerportal ausgeblendet')}
          tip={sensibel ? 'Rechtlich sensible Tickets zeigt das Portal nie' : 'Einsatz für den Eigentümer sichtbar machen bzw. ausblenden'} />
      </div>
      <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--ink3)' }}>
        {sensibel
          ? 'Rechtlich sensibel – im Eigentümerportal nie sichtbar.'
          : 'Der Eigentümer sieht Titel, Stand, Termine, Fotos mit „vorher/nachher“, abrechenbare Zeit und Material – keine Kommentare und keine internen Notizen. Es wird nichts verschickt.'}
      </span>

      {!sensibel && stand.fuer_eigentuemer_sichtbar && (
        <div className="flex flex-col" style={{ gap: 8, paddingTop: 6, borderTop: '1px solid var(--line)' }}>
          <label className="flex items-center" style={{ gap: 10 }}>
            <span className="flex-1" style={{ fontSize: 13.5, fontWeight: 700 }}>Geschätzte Kosten (brutto)</span>
            <input value={betrag} onChange={(e) => setBetrag(e.target.value)} onBlur={() => void betragSpeichern()}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void betragSpeichern(); } }}
              inputMode="decimal" placeholder="0,00" aria-label="Geschätzte Kosten in Euro" disabled={speichert || !!stand.kosten_entscheidung}
              data-tip={stand.kosten_entscheidung ? 'Erst die Entscheidung zurücksetzen, dann den Betrag ändern' : 'Über der Wertgrenze des Eigentümers wartet der Einsatz auf seine Freigabe im Portal'}
              style={{ width: 120, height: 36, borderRadius: 12, padding: '0 10px', textAlign: 'right', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>€</span>
          </label>
          {stand.kosten_entscheidung ? (
            <div className="flex items-start" style={{ gap: 8, padding: '9px 11px', borderRadius: 12,
              background: stand.kosten_entscheidung === 'freigegeben' ? 'var(--ok-soft)' : 'var(--bad-soft)', color: stand.kosten_entscheidung === 'freigegeben' ? 'var(--ok)' : 'var(--bad)' }}>
              {stand.kosten_entscheidung === 'freigegeben' ? <CheckCircle size={16} weight="fill" className="shrink-0" /> : <XCircle size={16} weight="fill" className="shrink-0" />}
              <span className="flex-1" style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4 }}>
                {stand.kosten_entscheidung === 'freigegeben' ? 'Vom Eigentümer freigegeben' : `Vom Eigentümer abgelehnt: „${stand.kosten_begruendung ?? ''}“ – Handwerker-Auftrag und Hausmeister-Freigabe sind gesperrt, bis zurückgesetzt (Angebot anfordern geht)`}
                {stand.kosten_entschieden_am ? ` · ${DATUM(stand.kosten_entschieden_am)}` : ''}
              </span>
              <button type="button" onClick={() => void zuruecksetzen()} disabled={speichert}
                data-tip="Entscheidung zurücksetzen – der Einsatz wartet dann wieder auf den Eigentümer (z. B. nach neuem Angebot)"
                className="inline-flex shrink-0 items-center" style={{ height: 28, padding: '0 10px', gap: 5, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
                <ArrowCounterClockwise size={12} weight="bold" /> Zurücksetzen
              </button>
            </div>
          ) : (
            <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--ink3)' }}>
              Liegt der Betrag über der Wertgrenze des Eigentümers, wartet der Einsatz auf seine Freigabe im Portal. Ohne Wertgrenze ist keine Freigabe nötig.
            </span>
          )}
        </div>
      )}
      {fehler && <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
    </WidgetHuelle>
  );
}
